// The employee directory as an assignable people list (user_id-bearing rows
// only — learning assigns to auth users). Same source the Learning admin and
// Team Learning pages already load; shared here so the session sheet's
// "Add people" picker and the wizard's explicit-people step reuse one fetch.
import { useQuery } from '@tanstack/react-query';
import { getEmployees } from '@/lib/hrApi';

const PAGE_SIZE = 100;
const MAX_PAGES = 30; // 3 000 employees — well above any tenant today; keeps a runaway loop impossible

export interface DirectoryPerson { user_id: string; id: string; name: string; department_id?: string | null; employment_type?: string | null }

export const usePeopleDirectory = (enabled = true) =>
  useQuery({
    queryKey: ['learning', 'people-directory'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<DirectoryPerson[]> => {
      // employee-service caps page_size at 100 (a larger value is a 422, which
      // used to leave every people picker silently empty) — walk the pages.
      const rows: any[] = [];
      for (let page = 1; page <= MAX_PAGES; page++) {
        const r: any = await getEmployees({ page, page_size: PAGE_SIZE });
        const items: any[] = r?.items || (Array.isArray(r) ? r : []);
        rows.push(...items);
        const totalPages = Number(r?.total_pages || 1);
        if (items.length < PAGE_SIZE || page >= totalPages) break;
      }
      return rows.filter((e) => e.user_id).map((e) => ({
        user_id: e.user_id, id: e.id, department_id: e.department_id ?? null, employment_type: e.employment_type ?? null,
        name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || String(e.id).slice(0, 8),
      }));
    },
  });

export interface DirectoryDepartment { id: string; name: string }
/** Departments for audience targeting (id + name). Same source SurveysAdmin uses. */
export const useDepartments = (enabled = true) =>
  useQuery({
    queryKey: ['learning', 'departments'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<DirectoryDepartment[]> => {
      const { getDepartments } = await import('@/lib/hrApi');
      const r: any = await getDepartments();
      return ((r?.items ?? r ?? []) as any[]).map((d) => ({ id: String(d.id), name: d.name || d.title || 'Department' }));
    },
  });

/** Employment types the audience rule can target (employee-service vocabulary). */
export const EMPLOYMENT_TYPES = [
  'full_time', 'part_time', 'casual', 'contract', 'fixed_term', 'specific_task', 'intern', 'seasonal', 'consultant', 'temporary', 'other',
] as const;
